import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, TooltipProps } from "recharts";
import { formatDecimal } from "../../utils";
import { CHART_COLORS } from "./chartTokens";
import { InformationHoverIcon } from "./InformationHoverIcon";

export type AutogestionSegments = {
  paqinfoCount: number;
  aiResolvedCount: number;
  enCursoCount: number;
  escaladoCount: number;
  totalConversations: number;
  autogestionRate: number;
};

type Props = {
  title?: string;
  subtitle?: string;
  info?: string;
  colSpan?: number;
  data: AutogestionSegments;
};

export const AUTOGESTION_SEGMENT_COLORS = {
  aiManaged: CHART_COLORS.primary,
  enCurso: "#D97706", // amber
  escalado: CHART_COLORS.destructive,
} as const;

const CustomTooltip = ({ active, payload }: TooltipProps<number, string>) => {
  if (!active || !payload || payload.length === 0) return null;
  const datum = payload[0];
  return (
    <div className="rounded-md border border-border bg-card p-3 shadow-md">
      <p className="text-sm font-medium text-palomma-secondary">
        {datum.name}: {datum.value}
      </p>
    </div>
  );
};

const AutogestionDonutChart = ({
  title = "Autogestión vs. Escalado",
  subtitle = "Conversaciones del período según cómo se resolvieron",
  info,
  colSpan = 1,
  data,
}: Props) => {
  // paqinfo (resolved without ticket) and aiResolved (AI ticket closed) are
  // both AI-handled end to end — shown as a single segment.
  const segments = [
    {
      key: "aiManaged",
      name: "Gestionado por IA",
      value: data.paqinfoCount + data.aiResolvedCount,
      color: AUTOGESTION_SEGMENT_COLORS.aiManaged,
    },
    {
      key: "enCurso",
      name: "En curso",
      value: data.enCursoCount,
      color: AUTOGESTION_SEGMENT_COLORS.enCurso,
    },
    {
      key: "escalado",
      name: "Escalado",
      value: data.escaladoCount,
      color: AUTOGESTION_SEGMENT_COLORS.escalado,
    },
  ];
  const visibleSegments = segments.filter((segment) => segment.value > 0);
  const hasNoData = data.totalConversations === 0;

  if (hasNoData) {
    return (
      <div style={{ gridColumn: colSpan ? `span ${colSpan}` : undefined }}>
        <div className="flex h-full min-h-[260px] flex-col rounded-2xl border border-border bg-card p-6">
          <div className="mb-4">
            <div className="flex items-center gap-1.5">
              <h3 className="text-sm font-semibold text-palomma-secondary">{title}</h3>
              {info && <InformationHoverIcon information={info} />}
            </div>
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </div>
          <div className="flex flex-1 items-center justify-center">
            <p className="text-sm text-muted-foreground">Sin datos en este período</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ gridColumn: colSpan ? `span ${colSpan}` : undefined }}>
      <div className="h-full rounded-2xl border border-border bg-card p-6">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-palomma-secondary">{title}</h3>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>

        <div className="relative mb-4 flex items-center justify-center">
          <div className="relative mx-auto h-[220px] w-full max-w-[250px] md:h-[250px] md:w-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={visibleSegments}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={90}
                  startAngle={90}
                  endAngle={-270}
                  dataKey="value"
                  nameKey="name"
                  stroke="none"
                  strokeLinecap="round"
                  isAnimationActive
                  animationDuration={300}
                  cornerRadius={10}
                  paddingAngle={2}
                  activeShape={{ opacity: 0.9 }}
                >
                  {visibleSegments.map((segment) => (
                    <Cell key={segment.key} fill={segment.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={false}
                  wrapperStyle={{ zIndex: 1000 }}
                />
              </PieChart>
            </ResponsiveContainer>

            <div className="pointer-events-none absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center">
              <p className="text-4xl font-bold leading-none">
                {formatDecimal(data.autogestionRate, 0)}%
              </p>
              <p className="mt-1 max-w-[60%] text-center text-sm">Autogestión</p>
            </div>
          </div>
        </div>

        <p className="mb-3 text-center text-sm text-muted-foreground">
          {data.paqinfoCount + data.aiResolvedCount} / {data.totalConversations} conversaciones
          autogestionadas
        </p>

        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          {segments.map((segment) => (
            <div key={segment.key} className="flex items-center">
              <div
                className="mr-2 size-2.5 rounded-full"
                style={{ backgroundColor: segment.color }}
              />
              <p className="text-sm text-palomma-secondary">
                {segment.name} ({segment.value})
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AutogestionDonutChart;
